import { useMemo, type ReactNode } from 'react';
import { formatDurationNumber, parseDuration, type DurationUnit } from '@forecastify/shared';
import { Segmented } from '@forecastify/ui';
import { useDuration } from './duration';
import { useI18n, useT, useTIn } from './i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 3 Nov 2026 · a duration FIELD says which unit its number is in.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product owner, overruling the first cut of DU-1: a field should not silently read what is
 * typed in the reader's unit. An *óra / nap* picker sits beside the number, starting on the
 * reader's preference, and says what THIS number is (`decisions.md` DU-2).
 *
 * ⚠️ **The picker relabels what was TYPED.** `5` with *nap* chosen and then *óra* is five hours —
 * the picker answers "what did I type", so moving it must not rewrite the typing. A box still
 * showing the SAVED figure is the exception (`retype`): it is redrawn in the new unit, or it would
 * claim a value nobody saved.
 *
 * ⚠️ **Grids have no picker** — a cell is a few characters wide. They read the reader's unit, and
 * an explicit `4h` or `1,5n` overrides it in either (`parseDuration`).
 */
export interface DurationIn {
  readonly unit: DurationUnit;
  /** Typed text to HOURS, in this field's unit; `null` when it is not a duration. */
  readonly parse: (text: string | number) => number | null;
  /** `7:30` / `0,94` — a draft to put in the box, in this field's unit. */
  readonly fmtNum: (hours: number | string) => string;
  /** A catalogue string, with the `.days` sibling when THIS field is in days. */
  readonly t: ReturnType<typeof useT>;
  /**
   * The box's text once the picker moves to `next`: the typing as it stands, or — when it is still
   * the `saved` figure, untouched — that figure in the new unit.
   */
  readonly retype: (text: string, next: DurationUnit, saved?: number | string) => string;
}

/** `chosen` is what the field's picker holds; `undefined` (never touched) is the reader's unit. */
export function useDurationIn(chosen: DurationUnit | undefined): DurationIn {
  const reader = useDuration();
  const { locale } = useI18n();
  const unit = chosen ?? reader.unit;
  const t = useTIn(unit);
  return useMemo<DurationIn>(
    () => ({
      unit,
      parse: (text) => parseDuration(text, unit),
      fmtNum: (hours) => formatDurationNumber(hours, unit, locale),
      t,
      retype: (text, next, saved) =>
        saved !== undefined && text === formatDurationNumber(saved, unit, locale)
          ? formatDurationNumber(saved, next, locale)
          : text,
    }),
    [unit, locale, t],
  );
}

/**
 * The *óra / nap* switch beside a duration box.
 *
 * ⚠️ **A mouse press does not take focus from the box.** Several of these fields save on blur and
 * close; without this, clicking *nap* would save the typing as hours and remove the field before
 * the click arrived. The keyboard still reaches it with Tab.
 */
export function DurationUnitPicker({
  unit,
  onChange,
  disabled = false,
}: {
  readonly unit: DurationUnit;
  readonly onChange: (unit: DurationUnit) => void;
  readonly disabled?: boolean;
}): ReactNode {
  const t = useT();
  return (
    <span
      className="contents"
      onMouseDown={(event) => {
        event.preventDefault();
      }}
    >
      <Segmented<DurationUnit>
        value={unit}
        label={t('duration.unit.label')}
        disabled={disabled}
        options={[
          { value: 'hours', label: t('duration.unit.hours') },
          { value: 'days', label: t('duration.unit.days') },
        ]}
        onChange={onChange}
      />
    </span>
  );
}
